// App.tsx
import React, { JSX, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { loadScript } from './utils/LoadScript';
import routes from './routes.json';
import Layout from './components/Layout';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetails from './pages/ProductDetails';
import Service from './pages/Service';
import ServiceDetails from './pages/ServiceDetails';
import Blogs from './pages/Blogs';
import BlogDetails from './pages/BlogDetails';
import Contact from './pages/Contact';
import Error from './pages/404';
import Faqs from './pages/Faqs';
import About from './pages/About';
import Career from './pages/Career';
import CareerDetails from './pages/CareerDetails';
import Teams from './pages/Teams';
import TeamDetails from './pages/TeamDetails';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import BusinessCard from './pages/BusinessCard';
import CaseStudies from './pages/CaseStudies';
import Security from './pages/Security';

const componentMap: Record<string, React.ComponentType<any>> = {
  "Home": Home,
  "Products": Products,
  "ProductDetails": ProductDetails,
  "Service": Service,
  "ServiceDetails": ServiceDetails,
  "Blogs": Blogs,
  "BlogDetails": BlogDetails,
  "Contact": Contact,
  "Faqs": Faqs,
  "About": About,
  "Career": Career,
  "CareerDetails": CareerDetails,
  "Teams": Teams,
  "TeamDetails": TeamDetails,
  "PrivacyPolicy": PrivacyPolicy,
  "TermsConditions": TermsConditions,
  "CaseStudies": CaseStudies,
  "Security": Security,
  "Error": Error
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const GoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-PKQF1N1WHS', {
        page_path: location.pathname,
      });
    }
    // Custom page tracking — fire-and-forget, never throws
    import('./services/api').then(({ trackPageview }) => {
      trackPageview(location.pathname);
    });
  }, [location]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // Skip loading main site scripts on business card page
    const isBusinessCardPage = window.location.pathname.startsWith('/card/');

    const loadScripts = async () => {
      if (isBusinessCardPage) return;

      // Every one of these was awaited in turn, so the browser downloaded them
      // one at a time — fifteen sequential round trips before the page became
      // interactive, on every route, including routes that use none of them.
      //
      // loadScript sets script.async = false, which makes the browser execute
      // tags in DOM insertion order regardless of which finishes downloading
      // first. So appending them all at once downloads in parallel while
      // preserving the order main.js depends on.
      const SCRIPTS = [
        '/assets/js/jquery-3.6.0.min.js',
        '/assets/js/bootstrap.bundle.min.js',
        '/assets/js/jquery.magnific-popup.min.js',
        '/assets/js/swiper-bundle.min.js',
        '/assets/js/counter.js',
        '/assets/js/progressbar.js',
        '/assets/js/gsap.min.js',
        '/assets/js/ScrollSmoother.min.js',
        '/assets/js/ScrollToPlugin.min.js',
        '/assets/js/ScrollTrigger.min.js',
        '/assets/js/SplitText.min.js',
        '/assets/js/jquery.meanmenu.min.js',
        '/assets/js/backToTop.js',
        '/assets/js/main.js',
        '/assets/js/error-handling.js',
      ];

      try {
        await Promise.all(SCRIPTS.map(loadScript));
      } catch (err) {
        console.error(err);
      }
    };

    const fetchToken = async () => {
      if (isBusinessCardPage) return;

      const existingToken = localStorage.getItem("access_token");
      if (!existingToken) {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/token`);
          const data = await res.json();
          if (data.token) {
            localStorage.setItem("access_token", data.token);
          }
        } catch (err) {
          console.error("Failed to fetch token:", err);
        }
      }
    };

    loadScripts();
    fetchToken();
  }, []);

  return (
    <Router>
      <GoogleAnalytics />
      <Routes>
        {/* Business Card - standalone page without Layout */}
        <Route path="/card/:uuid" element={<BusinessCard />} />

        <Route path="/" element={<Layout />}>
          {routes.map((route, index) => {
            const Component = componentMap[route.element];
            return (
              <Route
                key={index}
                path={route.path}
                element={Component ? <Component /> : <Error />}
              />
            );
          })}
          {/* Dynamic routes not in routes.json */}
          <Route path="/career-details/:id" element={<CareerDetails />} />
          <Route path="*" element={<Error />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;